import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { offerApi } from "../../services/offerApi";
import { DEFAULT_PARAMS } from "../../utils/offerCalc";
import ParamsFields from "./ParamsFields";

const formatDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("fr-FR");
};

// Fiche offres d'un client : consignes et paramètres par défaut des nouveaux chiffrages.
// Claude peut aussi la compléter via l'outil offers__client_profile_update.
const ClientProfilePanel = ({ profile, canEdit, onSaved, onApplyParams, onClose }) => {
  const [guidelines, setGuidelines] = useState(profile?.guidelines || "");
  const [params, setParams] = useState(profile?.default_params || null);
  const [status, setStatus] = useState("");

  // Recharger seulement quand la fiche change vraiment : le rafraîchissement périodique
  // de l'éditeur renvoie un nouvel objet identique qui effacerait la saisie en cours.
  const version = `${profile?.client_id}:${profile?.updated_at || ""}`;
  useEffect(() => {
    setGuidelines(profile?.guidelines || "");
    setParams(profile?.default_params || null);
  }, [version]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!profile) return null;

  const save = async () => {
    setStatus("Enregistrement…");
    try {
      const saved = await offerApi.updateClientProfile(profile.client_id, { guidelines, default_params: params });
      onSaved(saved);
      setStatus("Fiche enregistrée.");
    } catch (error) {
      setStatus(error.message);
    }
  };

  return (
    <aside className="w-full shrink-0 space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:w-96">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-slate-900">Fiche client · {profile.client_name}</h2>
        <button type="button" onClick={onClose} aria-label="Fermer la fiche client" className="rounded p-1 text-slate-400 hover:bg-slate-100">
          <X className="h-4 w-4" />
        </button>
      </div>
      <label className="block text-sm font-medium text-slate-700" htmlFor="client-guidelines">
        Consignes pour les offres
        <textarea
          id="client-guidelines"
          data-own-undo
          rows={10}
          disabled={!canEdit}
          value={guidelines}
          onChange={(event) => setGuidelines(event.target.value)}
          placeholder="Format des offres, ton, remise, délais, interlocuteurs…"
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
      </label>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">Paramètres par défaut</span>
          {canEdit && (
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <input type="checkbox" checked={Boolean(params)} onChange={(event) => setParams(event.target.checked ? { ...DEFAULT_PARAMS } : null)} />
              Définis
            </label>
          )}
        </div>
        {params ? (
          <ParamsFields params={params} disabled={!canEdit} onChange={(field, value) => setParams({ ...params, [field]: value })} compact />
        ) : (
          <p className="text-xs text-slate-500">Paramètres standard Innowide.</p>
        )}
      </div>
      {profile.updated_at && (
        <p className="text-xs text-slate-400">Mise à jour le {formatDate(profile.updated_at)}{profile.updated_by_name ? ` par ${profile.updated_by_name}` : ""}</p>
      )}
      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={save} className="rounded-full bg-slate-900 px-3 py-1.5 text-sm text-white">Enregistrer la fiche</button>
          {params && onApplyParams && (
            <button type="button" onClick={() => onApplyParams(params)} className="rounded-full px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100">
              Appliquer au chiffrage
            </button>
          )}
        </div>
      )}
      {status && <p className="text-xs text-slate-500">{status}</p>}
    </aside>
  );
};

export default ClientProfilePanel;
